import {
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';

import { Title } from '@angular/platform-browser';
import {
  LangChangeEvent,
  TranslateService
} from '@ngx-translate/core';

import { Subject } from 'rxjs';

import {
  finalize,
  takeUntil
} from 'rxjs/operators';

import { Article } from '../../core/models/article';
import { ArticleService } from '../../core/services/article.service';

@Component({
  selector: 'app-articles',
  templateUrl: './articles.component.html',
  styleUrls: ['./articles.component.scss']
})
export class ArticlesComponent
  implements OnInit, OnDestroy {

  articles: Article[] = [];

  isLoading = false;
  errorMessage = '';

  readonly sourceSystemId = 1;
  readonly statusId = 1;

  page = 1;
  pageSize = 6;

  total = 0;
  totalPages = 0;

  hasPrevious = false;
  hasNext = false;

  currentLang: 'ar' | 'en' = 'en';

  readonly defaultImage =
    'assets/images/article-placeholder.jpg';

  readonly articleImagesUrl =
    'https://applicationform.aldawal.com/images/';

  private readonly destroy$ =
    new Subject<void>();

  constructor(
    private titleService: Title,
    private articleService: ArticleService,
    private translateService: TranslateService
  ) { }

  ngOnInit(): void {
    this.initializeLanguage();
    this.loadArticles(1);
  }

  private initializeLanguage(): void {
    const savedLanguage =
      localStorage.getItem('lang') ||
      this.translateService.currentLang ||
      this.translateService.defaultLang ||
      'en';

    this.currentLang =
      savedLanguage === 'ar'
        ? 'ar'
        : 'en';

    this.setPageTitle();

    this.translateService.onLangChange
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(
        (event: LangChangeEvent) => {
          this.currentLang =
            event.lang === 'ar'
              ? 'ar'
              : 'en';

          this.setPageTitle();
        }
      );
  }

  private setPageTitle(): void {
    if (this.currentLang === 'ar') {
      this.titleService.setTitle(
        'الدول - المقالات'
      );

      return;
    }

    this.titleService.setTitle(
      'Aldawal - Articles'
    );
  }

  loadArticles(
    page: number = this.page
  ): void {
    if (page < 1) {
      return;
    }

    if (
      this.totalPages > 0 &&
      page > this.totalPages
    ) {
      return;
    }

    this.page = page;
    this.isLoading = true;
    this.errorMessage = '';

    this.articleService
      .getArticles(
        this.sourceSystemId,
        this.statusId,
        this.page,
        this.pageSize
      )
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: response => {
          this.articles =
            response?.items ?? [];

          this.page =
            response?.page ?? this.page;

          this.pageSize =
            response?.pageSize ??
            this.pageSize;

          this.total =
            response?.total ?? 0;

          this.totalPages =
            response?.totalPages ?? 0;

          this.hasPrevious =
            response?.hasPrevious ??
            this.page > 1;

          this.hasNext =
            response?.hasNext ??
            this.page < this.totalPages;
        },

        error: error => {
          console.error(
            'Articles API error:',
            error
          );

          this.articles = [];
          this.total = 0;
          this.totalPages = 0;
          this.hasPrevious = false;
          this.hasNext = false;

          const backendMessage =
            error?.error?.message?.content ||
            error?.error?.message ||
            error?.error?.title;

          this.errorMessage =
            backendMessage ||
            this.translateService.instant(
              'articlesLoadingErrorMessage'
            ) ||
            'حدث خطأ أثناء تحميل المقالات.';
        }
      });
  }

  previousPage(): void {
    if (
      this.isLoading ||
      !this.hasPrevious
    ) {
      return;
    }

    this.loadArticles(
      this.page - 1
    );
  }

  nextPage(): void {
    if (
      this.isLoading ||
      !this.hasNext
    ) {
      return;
    }

    this.loadArticles(
      this.page + 1
    );
  }

  goToPage(page: number): void {
    if (
      this.isLoading ||
      page === this.page ||
      page < 1 ||
      page > this.totalPages
    ) {
      return;
    }

    this.loadArticles(page);
  }

  get visiblePages(): number[] {
    const pages: number[] = [];

    if (this.totalPages <= 0) {
      return pages;
    }

    const maximumVisiblePages = 5;

    let startPage = Math.max(
      1,
      this.page - 2
    );

    let endPage = Math.min(
      this.totalPages,
      startPage +
      maximumVisiblePages -
      1
    );

    if (
      endPage - startPage + 1 <
      maximumVisiblePages
    ) {
      startPage = Math.max(
        1,
        endPage -
        maximumVisiblePages +
        1
      );
    }

    for (
      let pageNumber = startPage;
      pageNumber <= endPage;
      pageNumber++
    ) {
      pages.push(pageNumber);
    }

    return pages;
  }

  getArticleTitle(
    article: Article
  ): string {
    if (this.currentLang === 'ar') {
      return (
        article.titleAr ||
        article.titleEn ||
        article.title ||
        ''
      );
    }

    return (
      article.titleEn ||
      article.titleAr ||
      article.title ||
      ''
    );
  }

  getArticleSummary(
    article: Article
  ): string {
    if (this.currentLang === 'ar') {
      return (
        article.summaryAr ||
        article.summaryEn ||
        article.summary ||
        ''
      );
    }

    return (
      article.summaryEn ||
      article.summaryAr ||
      article.summary ||
      ''
    );
  }

  getArticleSlug(
    article: Article
  ): string {
    if (this.currentLang === 'ar') {
      return (
        article.slugAr ||
        article.slugEn ||
        article.slug ||
        article.id
      );
    }

    return (
      article.slugEn ||
      article.slugAr ||
      article.slug ||
      article.id
    );
  }

  getArticleImage(
    article: Article
  ): string {
    const image =
      article.featuredImage;

    if (!image) {
      return this.defaultImage;
    }

    if (
      image.startsWith('http://') ||
      image.startsWith('https://') ||
      image.startsWith('data:')
    ) {
      return image;
    }

    return `${this.articleImagesUrl}${image}`;
  }

  handleImageError(
    event: Event
  ): void {
    const imageElement =
      event.target as HTMLImageElement;

    if (
      !imageElement.src.includes(
        'article-placeholder.jpg'
      )
    ) {
      imageElement.src =
        this.defaultImage;
    }
  }

  trackByArticle(
    index: number,
    article: Article
  ): string {
    return article.id;
  }

  trackByPage(
    index: number,
    page: number
  ): number {
    return page;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}